<template>
  <v-container>
    <div>
      <h4 class="display-2 text-center">Search</h4>
      <br />
    </div>
    <v-row no-gutters>
      <v-text-field
        v-model="input.searchTerm"
        type="text"
        class="rounded-0 darken-1"
        name="search"
        placeholder="search"
        autofocus
        @keyup.enter="search"
        @keydown="resetSearchTimer"
        solo
        clearable
      />
      <v-btn @click="search" tile height="48" color="white" :loading="searchLoading">
        <v-icon>mdi-magnify</v-icon>
      </v-btn>
    </v-row>
    <v-tabs v-if="!$vuetify.breakpoint.xs" v-model="input.categoryIdx" grow show-arrows>
      <v-tab name="user" @click="changeCategory('users')"> Users </v-tab>
      <v-tab
        v-for="medium in Object.values($mediums)"
        v-bind:key="medium"
        name="medium"
        @click="changeCategory(medium)"
        >{{ userFriendlyMediumWord(medium, true) }}</v-tab
      >
    </v-tabs>
    <v-select
      v-else
      v-model="input.category"
      :items="
        [{ value: 'users', text: 'Users' }].concat(
          Object.values($mediums).map((medium) => ({
            value: medium,
            text: userFriendlyMediumWord(medium, true),
          })),
        )
      "
    />
    <v-divider v-if="resultsFound" />
    <div v-if="!resultsFound">No results Found</div>
    <v-list v-else id="queryResult">
      <template v-for="item in results">
        <v-list-item :key="item.id" class="pt-1 pb-1">
          <v-row align="center" no-gutters style="width: 100%">
            <v-col
              v-if="input.category !== 'users'"
              :cols="['film', 'book'].includes(input.category) ? 3 : 5"
              :sm="['film', 'book'].includes(input.category) ? 2 : 3"
              md="2"
              lg="1"
            >
              <div :class="['film', 'book'].includes(input.category) ? 'width-66' : 'width-100'">
                <v-img
                  :src="item.posterPath"
                  class="poster"
                  height="100"
                  contain
                  @click="navigateToEntry(item.id, input.category)"
                />
                <div v-if="username" class="d-flex justify-space-between" style="width: 100%">
                  <v-btn
                    v-if="!item.saved_for_later"
                    @click="
                      saveForLater(item.id, input.category);
                      item.saved_for_later = true;
                    "
                    color="accent"
                    tile
                    height="33"
                    width="50%"
                    min-width="0"
                    x-small
                  >
                    <v-icon>mdi-bookmark-outline</v-icon>
                  </v-btn>
                  <v-btn
                    v-else
                    @click="
                      removeFromWatchlist(item.id, input.category, false);
                      item.saved_for_later = false;
                    "
                    color="pink lighten-1"
                    tile
                    height="33"
                    width="50%"
                    min-width="0"
                    x-small
                  >
                    <v-icon>mdi-bookmark-minus</v-icon>
                  </v-btn>

                  <v-btn
                    v-if="!item.engaged"
                    @click="
                      markAsViewed(item.id, input.category);
                      item.engaged = true;
                    "
                    color="accent"
                    tile
                    height="33"
                    width="50%"
                    min-width="0"
                    x-small
                  >
                    <v-icon>mdi-eye-outline</v-icon>
                  </v-btn>
                  <v-btn
                    v-else
                    @click="
                      deleteFromViewed(item.id, input.category);
                      item.engaged = false;
                    "
                    color="pink lighten-1"
                    tile
                    height="33"
                    width="50%"
                    min-width="0"
                    x-small
                  >
                    <v-icon>mdi-eye-minus</v-icon>
                  </v-btn>
                </div>
              </div>
            </v-col>
            <profile-avatar
              v-else
              :size="50"
              :username="item.name"
              :preset-profile-pic="item.profilePicture"
            ></profile-avatar>
            <v-col
              :cols="['film', 'book'].includes(input.category) ? 9 : 7"
              :sm="['film', 'book'].includes(input.category) ? 4 : 3"
              md="3"
              lg="4"
              class="pl-2"
            >
              <button @click="navigateToEntry(input.category === 'users' ? item.name : item.id, input.category)">
                <strong>{{ item.name }}</strong>
              </button>
              <span v-if="input.category === $mediums.FILM && item.releaseDate">
                ({{ item.releaseDate.substring(0, 4) }})</span
              >
              <span v-if="(input.category === $mediums.BOOK || input.category === $mediums.ALBUM) && item.creators">
                -{{ item.creators.map((creator) => creator.name).join(", ") }}</span
              >
            </v-col>
            <v-col v-if="input.category !== 'users' && username" cols="6" sm="3" class="mt-1">
              <v-btn
                :to="{
                  path: '/rate/',
                  query: { medium: input.category, workID: item.id },
                }"
                color="primary"
              >
                Rate Work
              </v-btn>
            </v-col>
            <v-col cols="6" sm="3" class="mt-1">
              <v-btn
                v-if="input.category !== 'users'"
                @click="navigateToEntry(item.id, input.category)"
                color="primary"
              >
                {{ input.category }} Page
              </v-btn>
              <v-btn
                v-else-if="!$vuetify.breakpoint.xs"
                :to="{ path: '/profile/' + item.name }"
                class="justify-end"
                color="primary"
              >
                Profile
              </v-btn>
            </v-col>
          </v-row>
        </v-list-item>
        <v-divider :key="item.id + 'divider'" />
      </template>
    </v-list>
  </v-container>
</template>

<script>
import { mapMutations } from "vuex";
import watchlistAndSeen from "@/mixins/watchlistAndSeen";
import { userFriendlyMediumWord } from "@/util/mediumHelpers";

export default {
  name: "SearchPage",

  metaInfo: {
    title: "Search",
    description: "Find works by title or artist",
  },

  mixins: [watchlistAndSeen],

  data() {
    return {
      mediums: null,
      input: {
        searchTerm: "",
        category: "users",
        categoryIdx: 0,
      },
      results: [],
      resultsFound: true,
      timedSearch: undefined,
      searchLoading: false,
      latestTimeOfSearch: 0,
    };
  },

  computed: {
    username() {
      return this.$store.state.username;
    },
  },

  mounted() {
    this.input.searchTerm = this.$store.state.search.query;
    this.input.category = this.$store.state.search.category !== "" ? this.$store.state.search.category : "users";
    this.input.categoryIdx = this.categoryToIndex(this.input.category);
    this.results = this.$store.state.search.results;
    this.$vuetify.goTo(this.$store.state.search.scrollY, { duration: 0 });
  },

  beforeRouteLeave(to, from, next) {
    this.saveSearch({
      query: this.input.searchTerm,
      category: this.input.category,
      results: this.results,
      scrollY: document.documentElement.scrollTop,
    });

    next();
  },

  methods: {
    ...mapMutations(["saveSearch", "showSnackbar"]),

    async search() {
      if (!this.input.searchTerm) {
        return;
      }
      this.searchLoading = true;
      const timeOfSearch = Date.now();
      try {
        const response = await this.$axios.get("/v1/search", {
          params: {
            q: this.input.searchTerm,
            category: this.input.category,
            limit: 20,
          },
        });
        if (timeOfSearch < this.latestTimeOfSearch) {
          return;
        }
        this.latestTimeOfSearch = timeOfSearch;
        const results = response.data.items;
        this.resultsFound = results.length > 0;

        if (this.input.category === "users") {
          this.results = results.map((user) => ({
            name: user.username,
            id: user.id,
            profile_pic: user.profilePicture,
          }));
        } else {
          this.results = results.map((work) => ({
            name: work.title,
            id: work.id,
            posterPath: work.posterSource,
            creators: work.creators,
            engaged: work.userInfo ? work.userInfo.hasEngaged : undefined,
            saved_for_later: work.userInfo ? work.userInfo.savedForLater : undefined,
            releaseDate: work.releaseDate,
          }));
        }
        this.searchLoading = false;
      } catch (err) {
        console.error(err);
        this.showSnackbar({
          text: "an error occurred",
          error: true,
        });
        this.searchLoading = false;
      }
    },
    resetSearchTimer: function () {
      window.clearTimeout(this.timedSearch);
      this.timedSearch = window.setTimeout(this.search, 500);
    },
    changeCategory(category) {
      this.results.splice(0, this.results.length);
      this.input.category = category;
      if (this.input.searchTerm) {
        this.search();
      }
    },
    creatorsEqual(arr1, arr2) {
      if (arr1.length !== arr2.length) return false;
      for (let i = 0; i < arr1.length; i++) {
        if (arr1[i] !== arr2[i]) {
          return false;
        }
      }
      return true;
    },

    categoryToIndex: function (category) {
      let categories = ["users"].concat(Object.values(this.$mediums));
      return categories.indexOf(category);
    },

    async navigateToEntry(id, category) {
      if (category == "users") {
        await this.$router.push({ path: "profile" + "/" + id });
      } else {
        await this.navigateToWork(id, category);
      }
    },

    async navigateToWork(workId, medium) {
      await this.$router.push({
        name: "work",
        params: {
          medium,
          workId,
        },
      });
    },

    userFriendlyMediumWord,
  },
};
</script>

<style scoped>
.width-100 {
  width: 100px;
}
.width-66 {
  width: 66px;
}

.poster:hover {
  cursor: pointer;
}
</style>
